import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Container,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ContentType } from '../types';
import Navbar from '../components/Navbar';
import TextGenerationTab from '../components/TextGenerationTab';
import ImageGenerationTab from '../components/ImageGenerationTab';
import VideoGenerationTab from '../components/VideoGenerationTab';
import { connectWithAccessCode, disconnectUser, verifyNewAccessCode } from '../services/api';

interface AccessData {
  accessId: string;
  userNumber: number;
  codeName: string;
  maxUsers: number;
}

const ToolsPage: React.FC = () => {
  const [contentType, setContentType] = useState<ContentType>(() => {
    const savedContentType = localStorage.getItem('content_type');
    return (savedContentType as ContentType) || 'Normal';
  });
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('active_tab');
    return savedTab ? parseInt(savedTab) : 0;
  });
  const [showAccessDialog, setShowAccessDialog] = useState(true);
  const [accessCode, setAccessCode] = useState('');
  const [accessData, setAccessData] = useState<AccessData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 로컬 스토리지에서 액세스 데이터 복원
  useEffect(() => {
    const savedAccessData = localStorage.getItem('access_data');
    if (savedAccessData) {
      try {
        const parsedData = JSON.parse(savedAccessData);
        setAccessData(parsedData);
        setShowAccessDialog(false);
      } catch (error) {
        console.error('액세스 데이터 파싱 실패:', error);
        localStorage.removeItem('access_data');
      }
    }
  }, []);

  // 액세스 코드 유효성 실시간 검증
  useEffect(() => {
    if (!accessData) return;

    const verifyAccessCode = async () => {
      try {
        const accessCode = localStorage.getItem('access_code');
        if (!accessCode) {
          handleLogout();
          return;
        }

        await verifyNewAccessCode(accessCode);
      } catch (error: any) {
        console.log('액세스 코드 검증 실패:', error);
        if (error.response?.status === 404) {
          // 액세스 코드가 삭제되었거나 비활성화됨
          alert('액세스 코드가 삭제되었거나 비활성화되었습니다. 다시 로그인해주세요.');
          handleLogout();
        }
      }
    };

    // 5초마다 검증
    const interval = setInterval(verifyAccessCode, 5000);

    return () => clearInterval(interval);
  }, [accessData]);

  const handleContentTypeChange = (type: ContentType) => {
    setContentType(type);
    // 로컬 스토리지에 콘텐츠 타입 저장
    localStorage.setItem('content_type', type);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // 로컬 스토리지에 탭 상태 저장
    localStorage.setItem('active_tab', newValue.toString());
  };

  const handleAccessCodeSubmit = async () => {
    if (!accessCode.trim()) {
      setError('액세스 코드를 입력해주세요.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await connectWithAccessCode(accessCode.trim());
      const newAccessData = {
        accessId: response.access_id,
        userNumber: response.user_number,
        codeName: response.code_name,
        maxUsers: response.max_users,
      };
      setAccessData(newAccessData);
      
      // 로컬 스토리지에 저장
      localStorage.setItem('access_data', JSON.stringify(newAccessData));
      localStorage.setItem('access_code', accessCode.trim());
      
      setShowAccessDialog(false);
    } catch (error: any) {
      console.error('Access code connection failed:', error);
      if (error.response?.status === 404) {
        setError('유효하지 않은 액세스 코드입니다.');
      } else if (error.response?.status === 429) {
        setError('최대 사용자 수를 초과했습니다.');
      } else {
        setError('액세스 코드 연결에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleAccessCodeSubmit();
    }
  };

  // 로그아웃/연결 해제 함수
  const handleLogout = async () => {
    if (accessData) {
      try {
        await disconnectUser(accessData.accessId);
      } catch (error) {
        console.error('연결 해제 실패:', error);
      }
    }
    
    // 상태 초기화
    setAccessData(null);
    setShowAccessDialog(true);
    setAccessCode('');
    
    // 로컬 스토리지 정리
    localStorage.removeItem('access_data');
    localStorage.removeItem('access_code');
  };

  // 페이지 떠날 때 자동으로 연결 해제
  useEffect(() => {
    return () => {
      if (accessData) {
        disconnectUser(accessData.accessId).catch(console.error);
        // 로컬 스토리지에서도 제거
        localStorage.removeItem('access_data');
        localStorage.removeItem('access_code');
      }
    };
  }, [accessData]);

  if (showAccessDialog || !accessData) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          zIndex: 0,
        },
        zIndex: 1,
      }}>
        <Navbar />
        
        <Container
          maxWidth="sm"
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
            position: 'relative',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: '20px',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              width: '100%',
              maxWidth: '400px',
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                mb: 3,
                textAlign: 'center',
              }}
            >
              BNV AI Tools
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                mb: 3,
                textAlign: 'center',
              }}
            >
              액세스 코드를 입력하여 AI 도구를 사용해보세요.
            </Typography>

            <TextField
              fullWidth
              label="액세스 코드"
              variant="outlined"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              placeholder="액세스 코드 입력 (Enter로 접속)"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'white',
                  },
                },
              }}
            />

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  background: 'rgba(255, 0, 0, 0.1)',
                  color: 'white',
                  borderRadius: '12px',
                  '& .MuiAlert-icon': {
                    color: 'white',
                  },
                }}
              >
                {error}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleAccessCodeSubmit}
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                fontWeight: 'bold',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.3)',
                },
                '&:disabled': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : '접속하기'}
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
        zIndex: 0,
      },
      zIndex: 1,
    }}>
      <Navbar />

      <Container
        maxWidth={false}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          py: 1,
          px: { xs: 2, sm: 3, md: 4 },
          overflow: 'hidden',
          zIndex: 2,
          position: 'relative',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '20px',
            overflow: 'hidden',
            background: 'transparent',
            backdropFilter: 'none',
            border: 'none',
            boxShadow: 'none',
            maxWidth: '1800px',
            mx: 'auto',
            width: '100%',
          }}
        >
          <Box sx={{
            borderBottom: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3, md: 4 },
            py: 1,
            background: 'transparent',
            backdropFilter: 'none',
          }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: 'white',
                  height: 3,
                },
                '& .MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontWeight: 'bold',
                  minHeight: '48px',
                  '&.Mui-selected': {
                    color: 'white',
                  },
                },
              }}
            >
              <Tab
                label={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    텍스트 생성 AI
                  </Typography>
                }
                sx={{
                  background: 'transparent',
                  backdropFilter: 'none',
                  border: 'none',
                  borderRadius: '12px',
                  mr: 1,
                }}
              />
              <Tab
                label={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    이미지 생성 AI
                  </Typography>
                }
                sx={{
                  background: 'transparent',
                  backdropFilter: 'none',
                  border: 'none',
                  borderRadius: '12px',
                  mx: 1,
                }}
              />
              <Tab
                label={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    영상 생성 AI
                  </Typography>
                }
                sx={{
                  background: 'transparent',
                  backdropFilter: 'none',
                  border: 'none',
                  borderRadius: '12px',
                  ml: 1,
                }}
              />
            </Tabs>

            <Box sx={{ minWidth: 200, ml: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: 'white' }}>콘텐츠 타입</InputLabel>
                <Select
                  value={contentType}
                  label="콘텐츠 타입"
                  onChange={(e) => handleContentTypeChange(e.target.value as ContentType)}
                  sx={{
                    background: 'transparent',
                    backdropFilter: 'none',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'transparent',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'white',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.1)',
                          },
                          '&.Mui-selected': {
                            background: 'rgba(255, 255, 255, 0.2)',
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="Normal">일반</MenuItem>
                  <MenuItem value="AI 크리에이터">AI 크리에이터</MenuItem>
                  <MenuItem value="AI 윤리 & 사회변화">AI 윤리 & 사회변화</MenuItem>
                  <MenuItem value="차세대 기업가 정신">차세대 기업가 정신</MenuItem>
                  <MenuItem value="미래 사회 탐험가">미래 사회 탐험가</MenuItem>
                  <MenuItem value="미스터리 코드">미스터리 코드</MenuItem>
                  <MenuItem value="AI 트랜드 익스플로러">AI 트랜드 익스플로러</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box sx={{
            flex: 1,
            p: { xs: 1, sm: 2, md: 3 },
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {activeTab === 0 && (
              <TextGenerationTab 
                contentType={contentType} 
                accessId={accessData.accessId}
              />
            )}
            {activeTab === 1 && (
              <ImageGenerationTab 
                contentType={contentType} 
                accessId={accessData.accessId}
              />
            )}
            {activeTab === 2 && (
              <VideoGenerationTab 
                contentType={contentType}
                accessId={accessData.accessId}
              />
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ToolsPage; 